import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, Flag, Wallet, Truck, RefreshCw } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { ProductCard } from '../../components/shop/ProductCard';
import { SHOP_API, CATEGORIES, COLLECTIONS, BANNERS, img, PLACEHOLDER } from '../../components/shop/shopUtils';

export const ShopAnnouncement = ({ s }) => {
  const { t } = useLanguage();
  if (!s) return null;
  return (
    <div className="bg-[#0B0B0F] px-4 py-2 text-center text-xs text-white sm:text-sm" data-testid="shop-announcement">
      {t(s.announcement)}
      {s.announcement_pill?.en && <span className="ml-2 inline-block rounded-full bg-[#00E676] px-2 py-0.5 text-[11px] font-semibold text-black">{t(s.announcement_pill)}</span>}
    </div>
  );
};

export const useShopSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { fetch(`${SHOP_API}/settings`).then((r) => r.json()).then(setS).catch(() => {}); }, []);
  return s;
};

const PROMISES = [
  { icon: Flag, en: ['Made in Bangladesh', 'Produced by local makers'], bn: ['বাংলাদেশে তৈরি', 'স্থানীয় কারিগরদের তৈরি'] },
  { icon: Wallet, en: ['Pay your way', 'bKash, Nagad, cards, cash on delivery'], bn: ['আপনার সুবিধামতো পেমেন্ট', 'বিকাশ, নগদ, কার্ড, ক্যাশ অন ডেলিভারি'] },
  { icon: Truck, en: ['Nationwide delivery', '2–5 working days, all 64 districts'], bn: ['দেশজুড়ে ডেলিভারি', '২–৫ কর্মদিবস, ৬৪ জেলায়'] },
  { icon: RefreshCw, en: ['Easy returns', '7-day exchange on unworn items'], bn: ['সহজ রিটার্ন', 'অব্যবহৃত পণ্যে ৭ দিনের এক্সচেঞ্জ'] },
];

const Hero = ({ s }) => {
  const { t } = useLanguage();
  const slides = s?.hero_slides?.length ? s.hero_slides : [{ image: s?.hero_image }];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const id = setInterval(() => setI((x) => (x + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [slides.length]);
  const sl = slides[i];
  return (
    <section data-testid="shop-hero">
      <div className="relative w-full overflow-hidden bg-[#062E17]">
        <img src={img(sl.image)} onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} alt="NDP Official Collection" className="mx-auto max-h-[520px] w-full object-cover" />
      </div>
      <div className="shop-container py-8">
        <h1 className="shop-heading text-3xl sm:text-4xl">{sl.title ? t(sl.title) : t({ en: 'The NDP Official Collection', bn: 'এনডিপি অফিসিয়াল কালেকশন' })}</h1>
        <p className="mt-2 max-w-2xl text-[#6B7280]">{sl.subtitle ? t(sl.subtitle) : t({ en: 'Heritage tailoring, tricolour essentials and campaign gear — designed for supporters, made in Bangladesh.', bn: 'ঐতিহ্যবাহী পোশাক, ত্রিবর্ণ প্রয়োজনীয় পণ্য ও প্রচারণা সামগ্রী — সমর্থকদের জন্য নকশা করা, বাংলাদেশে তৈরি।' })}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a href="#products" className="shop-btn bg-[#E31E24] text-white hover:bg-[#c8181d]" data-testid="hero-shop-collection-btn">{t({ en: 'Shop the collection', bn: 'কালেকশন দেখুন' })}</a>
          <a href="#products" onClick={() => window.dispatchEvent(new CustomEvent('shop-best'))} className="shop-btn border border-[#E31E24] text-[#E31E24] hover:bg-[#FEE2E2]" data-testid="hero-best-sellers-btn">{t({ en: 'Best sellers', bn: 'বেস্ট সেলার' })}</a>
        </div>
      </div>
    </section>
  );
};

const PromoBanners = ({ s }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const list = BANNERS.filter((b) => s?.promo_banners?.[b.key]?.web && !(s.paused && b.key === 'gifts'));
  if (!list.length) return null;
  return (
    <section className="shop-container grid gap-4 pb-4 md:grid-cols-2" data-testid="promo-banners">
      {list.map((b) => (
        <button key={b.key} onClick={() => navigate(`/shop/collection/${b.col}`)} className="group overflow-hidden rounded-lg border border-[#E5E7EB] text-left" data-testid={`promo-banner-${b.key}`} aria-label={t(b.alt)}>
          <img src={img(s.promo_banners[b.key].web)} alt={t(b.alt)} loading="lazy" width="1600" height="640" className="aspect-[5/2] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
        </button>
      ))}
    </section>
  );
};

export default function Shop() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { category = '', collection = '' } = useParams();
  const s = useShopSettings();
  const [all, setAll] = useState(null);
  const [cat, setCat] = useState(category);
  const [col, setCol] = useState(decodeURIComponent(collection));
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('featured');
  const [best, setBest] = useState(false);

  useEffect(() => { setCat(category); setCol(decodeURIComponent(collection)); }, [category, collection]);
  useEffect(() => { fetch(`${SHOP_API}/products`).then((r) => r.json()).then(setAll).catch(() => setAll([])); }, []);
  useEffect(() => {
    const h = () => { setBest(true); setCat(''); setCol(''); };
    window.addEventListener('shop-best', h);
    return () => window.removeEventListener('shop-best', h);
  }, []);

  const list = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let r = (all || []).filter((p) => (!cat || p.cat === cat) && (!col || p.col === col) && (!best || p.best) &&
      (!ql || [p.name.en, p.name.bn, ...(p.spec || []).flat()].join(' ').toLowerCase().includes(ql)));
    if (sort === 'price_asc') r = [...r].sort((a, b) => a.price - b.price);
    if (sort === 'price_desc') r = [...r].sort((a, b) => b.price - a.price);
    if (sort === 'name') r = [...r].sort((a, b) => a.name.en.localeCompare(b.name.en));
    return r;
  }, [all, cat, col, q, sort, best]);

  const catImg = (id) => img((all || []).find((p) => p.cat === id)?.images?.[0]);
  const clear = () => { setQ(''); setCol(''); setCat(''); setBest(false); navigate('/shop'); };

  return (
    <div className="bg-white" data-testid="shop-page">
      <ShopAnnouncement s={s} />
      {s?.paused && <div className="bg-[#FEE2E2] px-4 py-3 text-center text-sm font-medium text-[#E31E24]" data-testid="shop-paused-notice">{t(s.pause_message)}</div>}
      {!category && !collection && <Hero s={s} />}
      {!category && !collection && <PromoBanners s={s} />}
      <div className="tricolour-rule" />

      <section className="shop-container py-10" data-testid="shop-categories">
        <h2 className="shop-heading mb-5 text-2xl">{category ? t(CATEGORIES.find((c) => c.id === category) || { en: 'Category', bn: 'বিভাগ' }) : t({ en: 'Shop by category', bn: 'বিভাগ অনুযায়ী কিনুন' })}</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
          {CATEGORIES.filter((c) => !(s?.paused && c.id === 'gifts')).map((c) => (
            <button key={c.id} onClick={() => { setCat(cat === c.id ? '' : c.id); setBest(false); }} data-testid={`category-tile-${c.id}`}
              className={`shop-card overflow-hidden text-left ${cat === c.id ? 'ring-2 ring-[#E31E24]' : ''}`}>
              <div className="aspect-[4/3] bg-[#F3F0EA]"><img src={catImg(c.id)} alt="" loading="lazy" className="h-full w-full object-cover" /></div>
              <p className="p-3 text-sm font-semibold leading-tight">{t(c)}</p>
            </button>
          ))}
        </div>
      </section>

      <section id="products" className="shop-container scroll-mt-24 pb-14" data-testid="shop-products">
        <div className="mb-5 flex flex-wrap gap-2" data-testid="collection-chips">
          {COLLECTIONS.filter((c) => !(s?.paused && c.id === 'Gifts')).map((c) => (
            <button key={c.id || 'all'} onClick={() => { setCol(c.id); setBest(false); }} data-testid={`collection-chip-${c.id ? c.id.toLowerCase().replace(/[^a-z]+/g, '-') : 'all'}`}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${col === c.id && !best ? 'border-[#E31E24] bg-[#FEE2E2] text-[#E31E24]' : 'border-[#E5E7EB] hover:border-[#0A0A0A]'}`}>{t(c)}</button>
          ))}
          {best && <button onClick={() => setBest(false)} className="rounded-full border border-[#E31E24] bg-[#FEE2E2] px-4 py-1.5 text-sm text-[#E31E24]" data-testid="best-filter-chip">{t({ en: 'Best sellers ×', bn: 'বেস্ট সেলার ×' })}</button>}
        </div>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <label className="flex flex-1 items-center gap-2 rounded-md border border-[#E5E7EB] px-3 focus-within:ring-2 focus-within:ring-[#00E676]">
            <Search className="h-4 w-4 text-[#6B7280]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t({ en: 'Search products, e.g. polo, flag, cotton', bn: 'পণ্য খুঁজুন, যেমন পোলো, পতাকা' })} className="w-full py-2.5 text-sm outline-none" data-testid="shop-search" aria-label="Search products" />
          </label>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm" data-testid="shop-sort" aria-label="Sort">
            <option value="featured">{t({ en: 'Featured', bn: 'ফিচার্ড' })}</option>
            <option value="price_asc">{t({ en: 'Price: low to high', bn: 'দাম: কম থেকে বেশি' })}</option>
            <option value="price_desc">{t({ en: 'Price: high to low', bn: 'দাম: বেশি থেকে কম' })}</option>
            <option value="name">{t({ en: 'Name', bn: 'নাম' })}</option>
          </select>
        </div>
        {all === null ? <p className="py-10 text-center text-[#6B7280]">…</p> : list.length === 0 ? (
          <div className="py-14 text-center" data-testid="shop-empty">
            <p className="text-[#6B7280]">{all.length === 0 ? t({ en: 'Products are being added. Please check back soon.', bn: 'পণ্য যোগ করা হচ্ছে। শীঘ্রই আবার দেখুন।' }) : t({ en: 'No products match. Clear the search or choose another collection.', bn: 'কোনো পণ্য মেলেনি। সার্চ মুছুন বা অন্য কালেকশন বেছে নিন।' })}</p>
            {all.length > 0 && <button onClick={clear} className="shop-btn mt-4 border border-[#E5E7EB]" data-testid="shop-clear-filters">{t({ en: 'Clear filters', bn: 'ফিল্টার মুছুন' })}</button>}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="product-grid">
            {list.map((p) => <ProductCard key={p._id} p={p} paused={s?.paused} />)}
          </div>
        )}
        <p className="mt-6 text-sm text-[#6B7280]">{t({ en: 'Ordering 50+ pieces for a party unit?', bn: 'দলীয় ইউনিটের জন্য ৫০+ পিস অর্ডার?' })} <button onClick={() => navigate('/shop/bulk-orders')} className="font-medium text-[#E31E24] underline" data-testid="bulk-order-link">{t({ en: 'Request a bulk quote', bn: 'বাল্ক কোটেশন চান' })}</button></p>
      </section>

      <div className="tricolour-rule" />
      <section className="bg-[#F9FAFB]" data-testid="promise-strip">
        <div className="shop-container grid grid-cols-2 gap-6 py-10 lg:grid-cols-4">
          {PROMISES.map((p, i) => (
            <div key={i} className="flex gap-3">
              <p.icon className="h-6 w-6 shrink-0 text-[#0E9A49]" />
              <div><p className="font-semibold">{(language === 'bn' ? p.bn : p.en)[0]}</p><p className="text-sm text-[#6B7280]">{(language === 'bn' ? p.bn : p.en)[1]}</p></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
